'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { CloseButton } from '../close-button/close-button';
import { Slot } from '../slot';
import { DrawerRootContext } from './context/drawer-root.context';
import { drawerCloseButtonStyles } from './styles/drawer-close-button.styles';
import type { DrawerCloseButtonPropsInterface } from './types/drawer-close-button.types';

/**
 * @since 10.22.0
 */
export const DrawerCloseButton = forwardRef<HTMLButtonElement, DrawerCloseButtonPropsInterface>(
  (props: DrawerCloseButtonPropsInterface, forwardedRef) => {
    const { children = <CloseButton />, className, css: cssProp, onClick, ...rest } = props;
    const {
      drawer: { onClickClose },
    } = useContext(DrawerRootContext);

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        onClickClose(event);
        onClick?.(event);
      },
      [onClickClose, onClick],
    );
    const isNativeElement = typeof children.type === 'string';

    return (
      <Slot
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Drawer.CloseButton"
        {...(isNativeElement
          ? {
              className: cx(
                'c11n_drawer__close-button',
                css(drawerCloseButtonStyles, cssProp),
                className,
              ),
            }
          : {
              css: { ...drawerCloseButtonStyles, ...cssProp },
              className: cx('c11n_drawer__close-button', className),
            })}
        {...rest}
        onClick={onClickHandler}
      >
        {children}
      </Slot>
    );
  },
);

DrawerCloseButton.displayName = 'Drawer.CloseButton';
